/**
 * Display formatters for the CRO forecast feature.
 *
 * The backend ships money as integer cents and percentages as numbers; the
 * FE never does arithmetic on those, only formats. Keeps the entire feature
 * one rounding convention.
 */

/** Format cents → "$1.23M" / "$12.3K" / "$1,234" / "—". */
export function fmtCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  const v = cents / 100;
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "M";
  if (Math.abs(v) >= 10_000) return "$" + (v / 1_000).toFixed(1) + "K";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** Format cents → "$201,450" / "$1,234,567" / "—". No K/M abbreviation,
 *  no decimals. Use for columns where exact precision matters — e.g. the
 *  Forecast Submission column on the Forecast Meeting tab needs to be
 *  comparable to HubSpot's Forecast Submission UI to the dollar. */
export function fmtCentsWhole(cents: number | null | undefined): string {
  if (cents == null) return "—";
  const v = Math.round(cents / 100);
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** Same as fmtCents but renders negative values as "$(123,456)". */
export function fmtCentsAccounting(cents: number | null | undefined): string {
  if (cents == null) return "—";
  if (cents < 0) {
    return "(" + fmtCents(Math.abs(cents)) + ")";
  }
  return fmtCents(cents);
}

export function fmtPct(value: number | null | undefined, decimals = 0): string {
  if (value == null) return "—";
  return value.toFixed(decimals) + "%";
}

export function fmtCount(n: number | null | undefined): string {
  if (n == null) return "—";
  return n.toLocaleString("en-US");
}

/** Monday of the given ISO (year, week) — using ISO calendar math. */
export function isoWeekStart(year: number, week: number): Date {
  // ISO week 1 contains Jan 4. Compute by finding Jan 4 of `year`, then
  // shifting to the Monday of that week, then adding (week - 1) × 7 days.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7; // 1..7, Sunday → 7
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1));
  const result = new Date(week1Monday);
  result.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return result;
}

/** Sunday of the given ISO week. */
export function isoWeekEnd(year: number, week: number): Date {
  const start = isoWeekStart(year, week);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return end;
}

/** "16 Mar – 22 Mar 2026" — compact week-range label for the filter bar. */
export function fmtIsoWeekRange(year: number, week: number): string {
  const start = isoWeekStart(year, week);
  const end = isoWeekEnd(year, week);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const fmt = (d: Date, withYear: boolean): string =>
    d.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: withYear ? "numeric" : undefined,
      timeZone: "UTC",
    });
  return sameMonth
    ? `${fmt(start, false).replace(/, \d+$/, "")} – ${fmt(end, true)}`
    : `${fmt(start, false)} – ${fmt(end, true)}`;
}

/** "Mar 23, 2026" or "—" for an ISO date string. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** ISO week number for a JS Date (Monday-start, week 1 contains Jan 4). */
export function isoWeekNumber(d: Date = new Date()): { year: number; week: number } {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = target.getUTCDay() || 7; // Sun=0 → 7
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: target.getUTCFullYear(), week };
}

/** "Wk 11 vs Wk 10" label for the week selector header. */
export function fmtWeekVs(curWeek: number, priorWeek: number): string {
  return `Wk ${curWeek} vs Wk ${priorWeek}`;
}

/** Month index → "March". */
export function fmtMonthName(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleString("en-US", { month: "long" });
}

export function fmtQuarter(year: number, quarter: number): string {
  return `Q${quarter} ${year}`;
}
